import { ToastType } from '@components';
import { OldLoadPayload } from '@store/loadboard';
import { fetchMaxRateRequests } from '@store/orders/services/rateRequestServices';
import { userStore } from '@store/user';
import { normalizeListMessage } from '@utils/appMessage';
import { handleToast } from '@utils/utils';
import groupBy from 'lodash/groupBy';
import size from 'lodash/size';
import { DateTime } from 'luxon';

import {
  fetchAllAnnouncement,
  fetchAllApprovals,
  fetchAllNeedPaperwork,
  fetchAllNotification,
  fetchAllPendingApprovals,
  fetchAllPendingChecks,
  fetchBillingholds,
  fetchCreditIncreaseRequestCustomers,
  fetchDisputedLoads,
  fetchDoNotSolicitRequests,
  fetchOldLoadsWarningData,
  fetchPendingCustomers,
  claimCreditIncreaseRequest,
  claimPendingCustomer,
} from './service';
import { fetchOpenEscalations } from './escalations';
import {
  setSidebarDataStore,
  setWarningDataStore,
  sidebarDataStore,
} from './store';
import {
  AllApprovalsTypes,
  ClaimCreditIncreasePayload,
  ClaimPendingCustomerPayload,
} from './types';

export const getAllAnnouncementData = async () => {
  try {
    const announcementDataList = await fetchAllAnnouncement();
    if (announcementDataList && announcementDataList.isSuccess) {
      setSidebarDataStore(
        'annoucementsData',
        'data',
        announcementDataList.value.map(normalizeListMessage),
      );
    }
  } finally {
    setSidebarDataStore('annoucementsData', 'loading', false);
  }
};

export const getAllPendingCustomers = async () => {
  try {
    const pendingCustomerList = await fetchPendingCustomers();
    if (pendingCustomerList) {
      setSidebarDataStore('pendingCustomers', 'data', pendingCustomerList);
    }
  } finally {
    setSidebarDataStore('pendingCustomers', 'loading', false);
  }
};

export const getCreditIncreaseRequestCustomers = async () => {
  try {
    const creditIncreaseRequestCustomers =
      await fetchCreditIncreaseRequestCustomers();
    if (creditIncreaseRequestCustomers) {
      setSidebarDataStore(
        'creditIncreaseRequestCustomers',
        'data',
        creditIncreaseRequestCustomers.sort(
          (x, y) =>
            new Date(y.requestDate).getTime() -
            new Date(x.requestDate).getTime(),
        ),
      );
    }
  } finally {
    setSidebarDataStore('creditIncreaseRequestCustomers', 'loading', false);
  }
};

export const claimOrUnclaimCreditIncrease = async (
  payload: ClaimCreditIncreasePayload,
): Promise<boolean> => {
  const ok = await claimCreditIncreaseRequest(payload);
  if (!ok) return false;

  // Optimistic update so the Claim cell flips immediately (ag-grid often
  // keeps cell renderers mounted across rowData refreshes).
  setSidebarDataStore('creditIncreaseRequestCustomers', 'data', (prev) =>
    prev.map((item) => {
      if (item.id !== payload.id) return item;
      if (payload.claim) {
        return {
          ...item,
          claimedBy: userStore.user.name,
          claimedDate: new Date().toISOString(),
          claimedByComment: payload.comment?.trim() || null,
        };
      }
      return {
        ...item,
        claimedBy: null,
        claimedDate: null,
        claimedByComment: null,
      };
    }),
  );

  await getCreditIncreaseRequestCustomers();
  return true;
};

export const claimOrUnclaimPendingCustomer = async (
  payload: ClaimPendingCustomerPayload,
): Promise<boolean> => {
  const ok = await claimPendingCustomer(payload);
  if (!ok) return false;

  setSidebarDataStore('pendingCustomers', 'data', (prev) =>
    prev.map((item) => {
      if (item.id !== payload.id) return item;
      if (payload.claim) {
        return {
          ...item,
          claimedBy: userStore.user.name,
          claimedDate: new Date().toISOString(),
          claimedByComment: payload.comment?.trim() || null,
        };
      }
      return {
        ...item,
        claimedBy: null,
        claimedDate: null,
        claimedByComment: null,
      };
    }),
  );

  await getAllPendingCustomers();
  return true;
};

export const getDoNotSolicitRequests = async () => {
  try {
    const doNotSolicitRequest = await fetchDoNotSolicitRequests();
    if (doNotSolicitRequest && doNotSolicitRequest.isSuccess) {
      setSidebarDataStore(
        'doNotSolicitRequests',
        'data',
        doNotSolicitRequest.value,
      );
    }
  } finally {
    setSidebarDataStore('doNotSolicitRequests', 'loading', false);
  }
};

export const getEscalations = async () => {
  try {
    const escalations = await fetchOpenEscalations();
    setSidebarDataStore('escalations', 'data', escalations);
  } finally {
    setSidebarDataStore('escalations', 'loading', false);
  }
};

export const getAllPendingChecksData = async () => {
  try {
    setSidebarDataStore('pendingChecksData', 'loading', true);
    const pendingCheckDataList = await fetchAllPendingChecks();
    if (pendingCheckDataList && pendingCheckDataList.isSuccess) {
      setSidebarDataStore(
        'pendingChecksData',
        'data',
        pendingCheckDataList.value,
      );
    }
  } finally {
    setSidebarDataStore('pendingChecksData', 'loading', false);
  }
};

let maxRateRequestsRefreshSequence = 0;

export const getMaxRateRequestsData = async () => {
  const requestId = ++maxRateRequestsRefreshSequence;
  try {
    setSidebarDataStore('maxRateRequestsData', 'loading', true);
    const rows = await fetchMaxRateRequests();
    if (requestId === maxRateRequestsRefreshSequence) {
      setSidebarDataStore('maxRateRequestsData', 'data', rows);
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, (error as Error).message);
  } finally {
    if (requestId === maxRateRequestsRefreshSequence) {
      setSidebarDataStore('maxRateRequestsData', 'loading', false);
    }
  }
};

export const getAllNeedPaperworkData = async () => {
  try {
    setSidebarDataStore('needPaperworkData', 'loading', true);
    const needPaperworkDataList = await fetchAllNeedPaperwork();
    if (needPaperworkDataList && needPaperworkDataList.isSuccess) {
      setSidebarDataStore(
        'needPaperworkData',
        'data',
        needPaperworkDataList.value,
      );
    }
  } finally {
    setSidebarDataStore('needPaperworkData', 'loading', false);
  }
};

export const getAllAlertsData = async () => {
  try {
    setSidebarDataStore('alertsData', 'loading', true);
    const alertsDataList = await fetchAllNotification();
    if (alertsDataList && alertsDataList.isSuccess) {
      setSidebarDataStore('alertsData', 'data', alertsDataList.value);
    }
  } finally {
    setSidebarDataStore('alertsData', 'loading', false);
  }
};

export const getAllApprovalsData = async () => {
  try {
    setSidebarDataStore('approvalsData', 'loading', true);

    const [approval, disputedLoads] = await Promise.all([
      fetchAllApprovals(),
      fetchDisputedLoads(),
    ]);

    if (approval && approval.isSuccess) {
      setSidebarDataStore('approvalsData', 'allApprovals', approval.value);
      approvalsByAgent();
      if (aboutToLockLoadboard(approval.value)) {
        handleToast(
          ToastType.Caution,
          'You have Approvals that will Lock your Loadboard in 1 day.  Please Address any Billing Approvals before then.',
        );
      }
      const overdueApprovals = getOverdueApprovalsFromAllApprovals(
        approval.value,
      );
      if (size(overdueApprovals) > 0) {
        setSidebarDataStore('overdueApprovals', 'data', overdueApprovals);
      }
    }

    if (disputedLoads && disputedLoads.isSuccess) {
      setSidebarDataStore(
        'approvalsData',
        'disputedLoads',
        disputedLoads.value,
      );
      disputesByAgent();
    }
  } finally {
    setSidebarDataStore('approvalsData', 'loading', false);
    setSidebarDataStore('approvalsData', 'loaded', true);
  }
};

export const aboutToLockLoadboard = (
  approvals: AllApprovalsTypes[],
): boolean => {
  const today = DateTime.local().startOf('day');

  return approvals.some((approval) => {
    if (!approval.setDate) return false;

    const setDate = DateTime.fromISO(approval.setDate).startOf('day');
    const lockDate = setDate.plus({ days: 13 }); // 13 days after setDate
    return (
      approval.needsAttention && approval.pastDue && lockDate.equals(today)
    );
  });
};

export const getOverdueApprovalsFromAllApprovals = (
  approvals: AllApprovalsTypes[],
): AllApprovalsTypes[] => {
  return approvals.filter(
    (approval) =>
      approval.agent === userStore.user.name &&
      approval.pastDue &&
      approval.needsAttention &&
      !approval.isDisputed,
  );
};

export const approvalsByAgent = () => {
  const grouping = groupBy(
    sidebarDataStore.approvalsData.allApprovals,
    'agent',
  );
  return grouping;
};

export const disputesByAgent = () => {
  const grouping = groupBy(
    sidebarDataStore.approvalsData.disputedLoads,
    'agent',
  );

  Object.values(grouping).forEach((approvals) => {
    approvals.sort((a) => (a.agent === a.lastCommentBy ? 1 : -1));
  });
  return grouping;
};

export const getAllPendingApprovals = async () => {
  try {
    setSidebarDataStore('approvals', 'loading', true);
    const approvals = await fetchAllPendingApprovals();
    if (approvals) {
      setSidebarDataStore('overdueApprovals', 'data', approvals);
      setSidebarDataStore('approvals', 'data', approvals);
    }
  } finally {
    setSidebarDataStore('approvals', 'loading', false);
  }
};

export const getAllBillingHolds = async () => {
  try {
    setWarningDataStore('billingHolds', 'loading', true);
    const data = await fetchBillingholds();
    if (data) {
      setWarningDataStore('billingHolds', 'data', data);
    }
  } finally {
    setWarningDataStore('billingHolds', 'loading', false);
  }
};

export const getAlloldloads = async (payload: OldLoadPayload) => {
  try {
    setWarningDataStore('oldLoad', 'loading', true);
    const data = await fetchOldLoadsWarningData(payload);
    if (data) {
      setWarningDataStore('oldLoad', 'data', data);
    }
  } finally {
    setWarningDataStore('oldLoad', 'loading', false);
  }
};
